import React, { useMemo, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import { useTable } from "react-table";
import { COLUMNS } from "./columns";
import {
  noDataPresent,
  InvoiceDetails,
  responseStatus,
  passwordConfirmModal,
  tooltips
} from "../../../../locales/en";
import { showToastType } from "../../../../utils/constants/appConstants";
import { formatDateWithDayjs, showToast } from "../../../../utils/utils";
import {
  FaPlusCircle,
  FaMinusCircle,
  FaRegTrashAlt,
  FaPlus,
  FaPencilAlt
} from "react-icons/fa";
import { PaymentModal } from "../../../modals/PaymentModal";
import PasswordConfModal from "../../../modals/PasswordConfModal";
import styles from "./../../../../assets/scss/custom.module.scss";
import "./paymenttable.scss";
import { Tooltip as ReactTooltip } from "react-tooltip";
import {
  getPasswordConfirmationURL,
  getPaymentsByQuoteURL,
  getPaymentsByRowIdURL
} from "../../../../utils/constants/apiEndPoints";
import privateAxios from "../../../../utils/axiosPrivate";
import LineSkeleton from "../../../skeletons/LineSkeleton";
const PaymentTable = ({ invData, onPaymentsLoaded }) => {
  const { id } = useParams();
  const [addPayModal, setAddPayModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [defaultValues, setDefaultValues] = useState({});
  const [editedRow, setEditedRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [enterPassword, setEnterPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [rowIdToDelete, setRowIdToDelete] = useState(null);
  const [totalAmount, setTotalAmount] = useState("");

  const tooltipsArray = [
    { id: "tooltip-addPayments", content: tooltips?.addpayments },
    { id: "tooltip-edit", content: tooltips?.edit },
    { id: "tooltip-delete", content: tooltips?.delete }
  ];

  const toggleAddPayModal = (rowData) => {
    if (rowData) {
      setEditMode(true);
      setDefaultValues(rowData);
      setEditedRow(rowData);
    } else {
      setEditMode(false);
      setDefaultValues({});
      setEditedRow(null);
    }
    setAddPayModal(!addPayModal);
  };

  // react table
  const columns = useMemo(() => COLUMNS, []);

  const loadPaymentData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await privateAxios.get(getPaymentsByQuoteURL(id));
      const responseData = response.data.data.payments;

      if (!responseData || responseData.length === 0) {
        setData([]);
        setTotalAmount(response.data.data.totalReceivedAmount);
        return;
      }

      responseData.sort((a, b) => {
        const dateDiff = new Date(b.paymentDate) - new Date(a.paymentDate);
        if (dateDiff !== 0) {
          return dateDiff;
        }
        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      setData(responseData);
      setTotalAmount(response.data.data.totalReceivedAmount);
      onPaymentsLoaded?.(responseData);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [privateAxios]);

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } =
    useTable({
      columns,
      data
    });

  const [expandedRows, setExpandedRows] = useState([]);

  const toggleRow = (rowId) => {
    if (expandedRows.includes(rowId)) {
      setExpandedRows(expandedRows.filter((id) => id !== rowId));
    } else {
      setExpandedRows([...expandedRows, rowId]);
    }
  };

  const isRowExpanded = (rowId) => expandedRows.includes(rowId);

  const deleteRow = async (rowId) => {
    if (!rowId) return;

    try {
      const response = await privateAxios.delete(getPaymentsByRowIdURL(rowId));
      const { message } = response.data;
      await loadPaymentData();
      showToast(message, responseStatus?.success);
    } catch (error) {
      showToast(error.response.data.message, showToastType.error);
    }
  };
  // Password Validation for Email =====>

  const handleDeleteData = (rowId) => {
    setRowIdToDelete(rowId);
    setEnterPassword(true);
  };

  const handlePasswordSubmit = async () => {
    try {
      const dataToSend = { password };
      const response = await privateAxios.post(
        getPasswordConfirmationURL,
        dataToSend
      );

      if (response.status === 200) {
        setEnterPassword(false);
        setPassword("");
        if (rowIdToDelete) {
          await deleteRow(rowIdToDelete);
          setRowIdToDelete(null);
        }
      } else {
        showToast(
          passwordConfirmModal?.validationMessage,
          responseStatus?.error
        );
      }
    } catch (error) {
      showToast(
        error.response?.data?.message || passwordConfirmModal?.validationFailed,
        responseStatus?.error
      );
    }
  };

  useEffect(() => {
    loadPaymentData();
  }, [loadPaymentData]);

  return (
    <>
      <table className="paymenttable" {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => {
            const { key, ...rest } = headerGroup.getHeaderGroupProps();
            return (
              <tr className="parentrow" {...rest} key={key}>
                <th />
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()} key={column.id}>
                    <span>{column.render("Header")}</span>
                  </th>
                ))}
                <th className="text-end" />
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {loading && (
            <tr>
              <td colSpan={columns.length + 2} className="text-center">
                <LineSkeleton />
              </td>
            </tr>
          )}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={columns.length + 2} className="text-center">
                {noDataPresent}
              </td>
            </tr>
          )}
          {!loading &&
            rows.length > 0 &&
            rows.map((row) => {
              prepareRow(row);
              const isExpanded = isRowExpanded(row.id);

              return (
                <React.Fragment key={row.id}>
                  <tr className="parentrow" {...row.getRowProps()}>
                    <td>
                      <span
                        className="expandbtn cursor-pointer"
                        onClick={() => toggleRow(row.id)}
                      >
                        {isExpanded ? <FaMinusCircle /> : <FaPlusCircle />}
                      </span>
                    </td>
                    {row.cells.map((cell) => (
                      <td
                        key={cell.getCellProps().key}
                        {...cell.getCellProps()}
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                    <td>
                      {row.original.type === "entered" && (
                        <ul
                          className={`${styles.btngroupwrap} d-flex justify-content-end align-items-center`}
                        >
                          <li>
                            <button
                              className={`${styles.btnPrimary} edit-btn added-icon`}
                              onClick={() => toggleAddPayModal(row.original)}
                              data-tooltip-id="tooltip-edit"
                              data-tooltip-content={tooltips.edit}
                            >
                              <FaPencilAlt />
                            </button>
                          </li>
                          <li>
                            <button
                              className={`${styles.btnPrimary} added-icon red-bg`}
                              onClick={() => handleDeleteData(row.original.id)}
                              data-tooltip-id="tooltip-delete"
                              data-tooltip-content={tooltips.delete}
                            >
                              <FaRegTrashAlt />
                            </button>
                          </li>
                        </ul>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="childrow colexpand">
                      <td colSpan={columns.length + 2}>
                        <div className="tdbody">
                          {columns.map((column) => (
                            <span
                              key={column.accessor}
                              data-label={column.Header}
                            >
                              {column.accessor === "paymentDate"
                                ? formatDateWithDayjs(
                                    row.original[column.accessor],
                                    "DD-MMM-YYYY"
                                  )
                                : String(row.original[column.accessor])}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
        </tbody>
      </table>

      <div className="row mt-2">
        <div className="col-12">
          <div
            className={`${styles.tableactions} d-flex align-items-center justify-content-between`}
          >
            <div className="leftcontent">
              <strong>
                {`${InvoiceDetails?.paymentSection?.paymentTable?.total}: `}
              </strong>
              <span className="totalamount">{totalAmount}</span>
            </div>
            <div className="rightcontent">
              <ul
                className={`${styles.btngroupwrap} d-flex justify-content-end align-items-center`}
              >
                <li>
                  <button
                    className={styles.btnPrimary}
                    onClick={() => toggleAddPayModal()}
                    data-tooltip-id="tooltip-addPayments"
                  >
                    <FaPlus />
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* for edit payment modal */}
      <PaymentModal
        isOpen={addPayModal}
        toggle={toggleAddPayModal}
        defaultValues={defaultValues}
        editMode={editMode}
        editedRow={editedRow}
        quoteId={id}
        invData={invData}
        loadPaymentData={loadPaymentData}
      />

      <PasswordConfModal
        title={InvoiceDetails?.paymentSection?.passwordContent?.title}
        content={InvoiceDetails?.paymentSection?.passwordContent?.subTitle}
        isOpen={enterPassword}
        onClose={() => setEnterPassword(false)}
        onSubmit={handlePasswordSubmit}
        password={password}
        setPassword={setPassword}
      />
      {tooltipsArray.map(({ id, content }) => (
        <ReactTooltip
          key={id}
          className="tooltipcustom"
          id={id}
          content={content}
          place="top"
        />
      ))}
    </>
  );
};
PaymentTable.propTypes = {
  invData: PropTypes.any,
  onPaymentsLoaded: PropTypes.func
};
export default PaymentTable;
